import axios, { AxiosRequestConfig, CancelToken, spread } from "axios";
import { CreateAuthHeader } from "../configurations/DefaultAxiosConfig";
import * as reqAxios from "../services/HttpService";
import ApiEndpoints, { IApiEndpoints } from "../configurations/ApiEndpoints";
import { IErrorModel } from "../models/DTO/ErrorModel";

type HttpVerbType = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export type RequestAggregateHandleType = {
    Verb: HttpVerbType,
    boddy?: any;
    Endpoint: (baseEndpoint: IApiEndpoints) => string,
    ResponseHandle: <TData = any>(response: reqAxios.HandledResponseType) => TData | TData[],
}

type SpreadAggregateResponseHandleType = /*(callback: (...args: reqAxios.HandledResponseType[]) => void) =>*/ (array: reqAxios.HandledResponseType[]) => void;

type DictVerbHandleprops<TData = any> = {
    endpoint: string,
    config: AxiosRequestConfig<TData>,
    boddy: any;
    handle: (response: reqAxios.HandledResponseType) => TData
};

type DictVerbType = {
    [identifier in HttpVerbType]: <TData = any>(props: DictVerbHandleprops<TData>) => Promise<reqAxios.HandledResponseType>
}

const HandleReponse = <TData = any>(response: reqAxios.HandledResponseType, handle: <TData>(response: reqAxios.HandledResponseType) => TData): reqAxios.HandledResponseType => {
    const result: reqAxios.HandledResponseType = { ...response, Data: handle<TData>(response) };
    return result;
}

const VerbHandle: DictVerbType = {
    GET: ({ endpoint, config, handle }: DictVerbHandleprops) =>
        reqAxios.GetAsync(endpoint, config, (response) => HandleReponse(response, handle)),
    POST: ({ endpoint, config, boddy, handle }: DictVerbHandleprops) =>
        reqAxios.PostAsync(endpoint, boddy!, config, (response) => HandleReponse(response, handle)),
    PUT: ({ endpoint, config, boddy, handle }: DictVerbHandleprops) =>
        reqAxios.PutAsync(endpoint, boddy!, config, (response) => HandleReponse(response, handle)),
    DELETE: ({ endpoint, config, boddy, handle }: DictVerbHandleprops) =>
        reqAxios.DeleteAsync(endpoint, boddy, config, (response) => HandleReponse(response, handle)),
    PATCH: ({ endpoint, config, boddy, handle }: DictVerbHandleprops) =>
        reqAxios.PatchAsync(endpoint, boddy, config, (response) => HandleReponse(response, handle))
}

const AggregateRequestHandle: (token: string, requestToAggregate: RequestAggregateHandleType[], spreadCallback: SpreadAggregateResponseHandleType, cancelToken?: CancelToken) => void =
    (token, requestToAggregate, spreadCallback, cancelToken) => {

        const baseEndpoint: IApiEndpoints = new ApiEndpoints(),
            config = CreateAuthHeader("Bearer", token, cancelToken),
            requests: Promise<reqAxios.HandledResponseType>[] = requestToAggregate
                .filter(req => req != null && req.Endpoint != null && req.ResponseHandle != null)
                .map(req => {
                    const { Verb: verb, boddy, Endpoint: endpoint, ResponseHandle: handle } = req,
                        handleObj = { boddy, config, endpoint: endpoint(baseEndpoint), handle },
                        result: Promise<reqAxios.HandledResponseType> = VerbHandle[verb](handleObj);
                    return result;
                });

        reqAxios.All<reqAxios.HandledResponseType>(requests, spreadCallback);
    }

export const AggregateResponseHandle = <TData>(response: reqAxios.HandledResponseType<TData>, updateErrorState: ((error?: IErrorModel | undefined) => void) | undefined, callback: (data?: TData) => void): boolean => {

    let result = false;

    if (response != null) {
        const { Error, Data } = response;

        if (Error != null) {
            result = true;
            updateErrorState && updateErrorState(Error)
        }

        if (!result && Error == null) {
            callback(Data as TData)
        }
    }

    return result;
}

export const AggregateResponseWarningOrErrorHandle = (responses: reqAxios.HandledResponseType[]): { Messages: string[], Errors: IErrorModel[] } => {
    const lstMessages: string[] = responses
        .map(response => response.Messages || [])
        .reduce((acc, lst) => acc.concat(lst), []),
        lstErrors: IErrorModel[] = responses
            .map(resp => resp.Error)
            .filter(x => x != null)
            .map(x => x as IErrorModel),

        result = {
            Messages: lstMessages.filter(x => x != null && x != ""),
            Errors: lstErrors.filter(x => x != null)
        };

    return result;
}

export default AggregateRequestHandle